import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@dc-shared-web/lib'

const badgeVariants = cva(
  'tw-inline-flex tw-items-center tw-rounded-lg [&_svg]:tw-pr-1 tw-break-normal focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-ring focus:tw-ring-offset-2',
  {
    variants: {
      variant: {
        default: '',
        soft: '',
        outline: 'tw-border tw-border-solid',
        surface: 'tw-border tw-border-solid',
      },
      color: {
        primary: '',
        blue: '',
        yellow: '',
        green: '',
        red: '',
        purple: '',
      },
      size: {
        sm: 'tw-text-xs [&_svg]:tw-text-xs tw-py-0.5 tw-px-2',
        md: 'tw-text-sm [&_svg]:tw-text-sm tw-py-0.5 tw-px-2',
        lg: 'tw-text-base [&_svg]:tw-text-base tw-py-1 tw-px-3',
      },
    },
    compoundVariants: [
      {
        variant: 'default',
        color: 'primary',
        className:
          'tw-bg-secondary tw-text-neutral-950 [&_svg]:tw-text-neutral-950',
      },
      {
        variant: 'default',
        color: 'blue',
        className: 'tw-bg-primary tw-text-white [&_svg]:tw-text-white',
      },
      {
        variant: 'default',
        color: 'yellow',
        className:
          'tw-bg-warning-strong tw-text-neutral-950 [&_svg]:tw-text-neutral-950',
      },
      {
        variant: 'default',
        color: 'green',
        className: 'tw-bg-success tw-text-white [&_svg]:tw-text-white',
      },
      {
        variant: 'default',
        color: 'red',
        className: 'tw-bg-destructive tw-text-white [&_svg]:tw-text-white',
      },
      {
        variant: 'default',
        color: 'purple',
        className: 'tw-bg-magic tw-text-white [&_svg]:tw-text-white',
      },
      {
        variant: 'soft',
        color: 'primary',
        className:
          'tw-bg-secondary tw-text-neutral-950 [&_svg]:tw-text-neutral-950',
      },
      {
        variant: 'soft',
        color: 'blue',
        className: 'tw-bg-secondary tw-text-primary [&_svg]:tw-text-blue-600',
      },
      {
        variant: 'soft',
        color: 'yellow',
        className:
          'tw-bg-yellow-50 tw-text-yellow-700 [&_svg]:tw-text-yellow-700',
      },
      {
        variant: 'soft',
        color: 'green',
        className: 'tw-bg-green-50 tw-text-green-700 [&_svg]:tw-text-green-700',
      },
      {
        variant: 'soft',
        color: 'red',
        className:
          'tw-bg-destructive-50 tw-text-destructive-600 [&_svg]:tw-text-destructive-600',
      },
      {
        variant: 'soft',
        color: 'purple',
        className:
          'tw-bg-purple-50 tw-text-purple-600 [&_svg]:tw-text-purple-600',
      },
      {
        variant: 'outline',
        color: 'primary',
        className:
          'tw-bg-white tw-border-neutral-200 tw-text-neutral-500 [&_svg]:tw-text-neutral-500',
      },
      {
        variant: 'outline',
        color: 'blue',
        className:
          'tw-bg-white tw-border-primary-400 tw-text-primary-600 [&_svg]:tw-text-primary-600',
      },
      {
        variant: 'outline',
        color: 'yellow',
        className:
          'tw-bg-white tw-border-yellow-400 tw-text-yellow-700 [&_svg]:tw-text-yellow-700',
      },
      {
        variant: 'outline',
        color: 'green',
        className:
          'tw-bg-white tw-border-green-500 tw-text-green-700 [&_svg]:tw-text-green-700',
      },
      {
        variant: 'outline',
        color: 'red',
        className:
          'tw-bg-white tw-border-destructive-400 tw-text-destructive-600 [&_svg]:tw-text-destructive-600',
      },
      {
        variant: 'outline',
        color: 'purple',
        className:
          'tw-bg-white tw-border-purple-400 tw-text-purple-600 [&_svg]:tw-text-purple-600',
      },
      {
        variant: 'surface',
        color: 'primary',
        className:
          'tw-bg-neutral-50 tw-border-neutral-200 tw-text-neutral-500 [&_svg]:tw-text-neutral-500',
      },
      {
        variant: 'surface',
        color: 'blue',
        className:
          'tw-bg-blue-50 tw-border-primary-400 tw-text-primary-600 [&_svg]:tw-text-primary-600',
      },
      {
        variant: 'surface',
        color: 'yellow',
        className:
          'tw-bg-yellow-50 tw-border-yellow-400 tw-text-yellow-700 [&_svg]:tw-text-yellow-700',
      },
      {
        variant: 'surface',
        color: 'green',
        className:
          'tw-bg-green-50 tw-border-green-500 tw-text-green-700 [&_svg]:tw-text-green-700',
      },
      {
        variant: 'surface',
        color: 'red',
        className:
          'tw-bg-red-50 tw-border-destructive-400 tw-text-destructive-600 [&_svg]:tw-text-destructive-600',
      },
      {
        variant: 'surface',
        color: 'purple',
        className:
          'tw-bg-purple-50 tw-border-purple-400 tw-text-purple-600 [&_svg]:tw-text-purple-600',
      },
    ],
    defaultVariants: {
      variant: 'default',
      color: 'primary',
      size: 'md',
    },
  }
)

export interface BadgeProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'color'>,
    VariantProps<typeof badgeVariants> {}

function Badge({ className, variant, color, size, ...props }: BadgeProps) {
  return (
    <div
      className={cn(badgeVariants({ variant, color, size }), className)}
      {...props}
    />
  )
}

export { Badge, badgeVariants }
